import React, { useEffect, useRef, useState } from 'react'

import { observer } from 'mobx-react'

import HospitalHomeLayout from 'presentation/hospital/layouts/Home'
import { useStores } from 'presentation/hooks/use-stores'
import moment from 'moment-timezone'
import { toast } from 'react-toastify'
import LoadingModal from 'presentation/shared/components/LoadingModal'
import { GetCrmoStatusCounters } from 'domain/usecases/surgical-order/get-crmo-status-counters'
import { GetCrmoPendencyCounters } from 'domain/usecases/surgical-order/get-crmo-pendency-counters'
import { GetSurgicalOrderSchedulingCounter } from 'domain/usecases/surgical-order/get-surgical-order-scheduling-counter'
import { useServices } from 'presentation/hooks/use-services'

const HospitalHome = observer(() => {
  const surgicalOrderService = useServices().surgicalOrder
  const [statusCounters, setStatusCounters] =
    useState<GetCrmoStatusCounters.Model>()
  const [pendencyCounters, setPendencyCounters] =
    useState<GetCrmoPendencyCounters.Model>()
  const [schedulingTotalizers, setSchedulingTotalizers] =
    useState<GetSurgicalOrderSchedulingCounter.Model>()
  const [showLoadingModal, setShowLoadingModal] = useState<boolean>(false)
  const lastRequestId = useRef(0)

  const hospital = useStores().currentHospital?.actualHospital
  const currentFilters = useStores().currentFilters
  const crmoFilters = currentFilters.crmoFilter
  const hospitalName = hospital?.friendly_name || hospital?.name

  const getSurgicalCount = async (
    startDate: string,
    endDate: string,
    label?: string
  ) => {
    currentFilters.setCrmoFilter({
      end: endDate,
      start: startDate,
      label
    })
    const requestId = ++lastRequestId.current
    setShowLoadingModal(true)

    const params = { startDate, endDate }
    const [statusResult, pendencyResult, schedulingResult] =
      await Promise.allSettled([
        surgicalOrderService.getCrmoStatusCounters(params),
        surgicalOrderService.getCrmoPendencyCounters(params),
        surgicalOrderService.getSurgicalOrderSchedulingCounter(params)
      ])

    // A newer period was requested meanwhile: ignore this response
    if (requestId !== lastRequestId.current) return

    // A failed section is cleared so it never shows the previous period's numbers
    setStatusCounters(
      statusResult.status === 'fulfilled' ? statusResult.value : undefined
    )
    setPendencyCounters(
      pendencyResult.status === 'fulfilled' ? pendencyResult.value : undefined
    )
    setSchedulingTotalizers(
      schedulingResult.status === 'fulfilled'
        ? schedulingResult.value
        : undefined
    )

    if (
      [statusResult, pendencyResult, schedulingResult].some(
        (result) => result.status === 'rejected'
      )
    ) {
      toast.error('Ocorreu um erro ao tentar carregar os totalizadores')
    }
    setShowLoadingModal(false)
  }

  useEffect(() => {
    if (crmoFilters?.end && crmoFilters?.start) {
      getSurgicalCount(crmoFilters?.start, crmoFilters?.end, crmoFilters?.label)
    } else {
      getSurgicalCount(
        moment().utc().startOf('day').toISOString(),
        moment().utc().endOf('day').toISOString()
      )
    }
  }, [])

  return (
    <>
      <HospitalHomeLayout
        hospitalName={hospitalName}
        changeFilter={getSurgicalCount}
        counters={statusCounters}
        pendencyCounters={pendencyCounters}
        schedulingCounters={schedulingTotalizers}
        crmoDefaultFilters={
          crmoFilters || {
            end: moment().utc().endOf('day').toISOString(),
            start: moment().utc().startOf('day').toISOString()
          }
        }
      />
      <LoadingModal show={showLoadingModal} />
    </>
  )
})

export default HospitalHome
